import { MobileCarousel } from '../MobileCarousel';
import { NonMobileProductCarousel } from '../NonMobileCarousel';
import { ProductDetailsSectionT } from '../../../../lib/types/sections';
import { useAppSelector } from '../../../../lib/store/hooks';
import { SectionName } from '../../../../lib/models/Section';
import { useLocation, useParams } from 'react-router-dom';
import { findNextStatusLevel } from '../../../endUserProductRecommendation/helpers';
import { useCalculatePoints } from '../../../../lib/hooks/endUserHooks/useCalculatePoints';
import { useContainerQuery } from '../../../../lib/hooks/endUserHooks/useContainerQuery';
import { useEffect, useRef } from 'react';
import { ProductT } from '../../../../lib/types/product';
import { IconT } from '../../../designSettings/types';
import { FinderButton } from '../../../../components/FinderButton';
import { useGetCampaignObjectPositions } from '../../../../lib/hooks/endUserHooks/useGetCampaignObjectPositions';
import {
  CAMPAIGN_POSITION_ELEMENT_OPTIONS,
  CAMPAIGN_POSITION_PAGES_OPTIONS,
  CAMPAIGN_POSITION_SECTION_OPTIONS,
  SUB_ELEMENT_TYPE,
} from '../../../campaignDetails/hooks/useGetPositionOption';
import { prepareProducts, sortByIrrelevantSurveyReason } from './helpers';

export const EndUserProductSection = () => {
  const { id: demandAreaId } = useParams();

  const {
    selectedCategory,
    allProducts,
    sections,
    brandKit,
    wishlistProducts,
    demandArea,
    isGuestMode,
    statusLevels,
  } = useAppSelector(({ endUser }) => endUser);

  const { foundObjects } = useGetCampaignObjectPositions({
    page: CAMPAIGN_POSITION_PAGES_OPTIONS.PRODUCT_PAGE,
    sections: [CAMPAIGN_POSITION_SECTION_OPTIONS.PRODUCT_LIST],
    elements: [
      CAMPAIGN_POSITION_ELEMENT_OPTIONS.BULLET_POINT,
      CAMPAIGN_POSITION_ELEMENT_OPTIONS.HEADLINE,
    ],
  });

  const userPoints = useCalculatePoints(allProducts);

  const productSectionContent = sections?.[SectionName.PRODUCT_DETAILS];

  const irrelevantProducts = allProducts.filter(
    (product: ProductT) => product.irrelevantSurveyReason !== null,
  );
  const productWithoutIrrelevant = allProducts.filter(
    (product: ProductT) => product.irrelevantSurveyReason === null,
  );

  const preparedProducts = prepareProducts(
    productWithoutIrrelevant,
    irrelevantProducts,
    foundObjects.filter(
      (obj) => obj.type === CAMPAIGN_POSITION_ELEMENT_OPTIONS.BULLET_POINT,
    ),
    foundObjects.filter((obj) => obj.type === SUB_ELEMENT_TYPE.PRODUCT_HEADLINE),
  );

  const foundProducts = preparedProducts.filter(
    (product) =>
      product.demandAreaId === Number(demandAreaId) &&
      product.category.id === selectedCategory?.id,
  );

  const modifiedProducts = foundProducts.map((product) => ({
    ...product,
    category: { ...selectedCategory },
    reason: findNextStatusLevel(statusLevels, userPoints, product),
    isWishlist: !!wishlistProducts.find(
      (wishlistProduct) => wishlistProduct.id === product.id,
    ),
  }));

  const sortedProducts: any = modifiedProducts
    .slice()
    .sort((a, b) => a.priority - b.priority);

  const sortedByIrrelevantReason = sortByIrrelevantSurveyReason(sortedProducts);

  const productSection = {
    arePointsShown: true,
    isPriceShown: true,
    pointsIcon: demandArea?.project.pointsIcon as IconT,
    products: sortedByIrrelevantReason,
    ...productSectionContent,
  } as ProductDetailsSectionT;

  const { isMobile } = useContainerQuery();

  const fieldRef = useRef<HTMLDivElement | null>(null);

  const { state } = useLocation();

  useEffect(() => {
    if (state?.scrollTo === SectionName.PRODUCT_DETAILS && fieldRef.current) {
      setTimeout(() => {
        fieldRef?.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 0);
    }
  }, [state]);

  return (
    <div ref={fieldRef}>
      {isMobile ? (
        <MobileCarousel
          brandKit={brandKit}
          productSection={productSection}
          isGuestMode={isGuestMode}
        />
      ) : (
        <NonMobileProductCarousel
          brandKit={brandKit}
          productSection={productSection}
          isGuestMode={isGuestMode}
        />
      )}
      <FinderButton
        showFinderButton={selectedCategory?.finderEnabled}
        finderButtonText={selectedCategory?.finderButtonName}
        finderButtonUrl={selectedCategory?.finderUrl}
        recommendForAllCategories={false}
        buttonType="secondary"
        categoryId={selectedCategory?.id}
        style={{ textAlign: 'center', marginTop: 16 }}
      />
    </div>
  );
};
